import { useState } from "react";
import { SettingsShell, useSettings, SaveBar, ToggleRow, CheckboxRow } from "./SettingsShell";

/**
 * Estimates settings — two sub-tabs: Configuration + Customer view.
 * Customer view toggles configure the on-screen PREVIEW area on this settings page only.
 * The actual multi-option estimate PDF/preview code is untouched (per user directive).
 */

const DEFAULT_CV = {
  fields: {
    job_number: true,
    estimate_number: true,
    service_date: true,
    expiration_date: true,
    estimate_date: true,
    summary_of_work: true,
    business_name: true,
    technician_name: true,
    customer_display_name: true,
    customer_company_name: false,
    // Services subgroup
    services_line_items: true,
    services_name: true,
    services_description: true,
    services_quantity: true,
    services_unit_price: true,
    services_line_amount: true,
    services_subtotal: true,
    // Materials subgroup
    materials_line_items: false,
    materials_subtotal: false,
  },
  view_format: "email", // "email" | "envelope"
};

export default function SettingsEstimates() {
  const { settings, patch, save, loading, saving, saved, err } = useSettings();
  const [activeSubTab, setActiveSubTab] = useState("configuration");
  if (loading || !settings) return <div className="p-6 text-sm text-slate-500">Loading…</div>;

  return (
    <SettingsShell
      title="Estimates"
      subtitle="Configure how estimates are created, approved, and shown to customers."
      testid="settings-estimates"
      subTabs={[
        { key: "configuration", label: "Configuration" },
        { key: "customer-view", label: "Customer view" },
      ]}
      activeSubTab={activeSubTab}
      onSubTabChange={setActiveSubTab}
    >
      {activeSubTab === "configuration" && <ConfigurationTab settings={settings} patch={patch} />}
      {activeSubTab === "customer-view" && <CustomerViewTab settings={settings} patch={patch} />}
      <SaveBar onSave={save} saving={saving} saved={saved} err={err} testid="settings-estimates" />
    </SettingsShell>
  );
}

function ConfigurationTab({ settings, patch }) {
  return (
    <div className="space-y-6">
      {/* Deposit payments — coming soon banner */}
      <section data-testid="est-section-deposits" className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider bg-[#0F4C81] text-white px-1.5 py-0.5 rounded">New</span>
              <h2 className="font-display font-semibold text-slate-900">Deposit payments</h2>
            </div>
            <p className="text-sm text-slate-500 mt-1">Collect deposits from customers when they approve an estimate.</p>
          </div>
          <button
            type="button"
            disabled
            data-testid="est-connect-bank"
            className="px-3 py-1.5 border border-slate-300 text-slate-400 text-sm rounded-md cursor-not-allowed"
            title="Requires Stripe integration — coming soon"
          >
            Connect my bank
          </button>
        </div>
      </section>

      <section data-testid="est-section-configure" className="bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="font-display font-semibold text-slate-900 mb-4">Configure Estimates</h2>

        <div className="flex items-center justify-between py-3 border-b border-slate-100">
          <div className="flex-1">
            <span className="text-sm font-medium text-slate-900">Default expiration</span>
            <p className="text-xs text-slate-500 mt-0.5">Estimates automatically expire this many days after they&apos;re sent.</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={365}
              data-testid="est-default-expiration"
              value={settings.default_estimate_expire_days || 30}
              onChange={(e) => patch({ default_estimate_expire_days: parseInt(e.target.value) || 30 })}
              className="w-20 px-2 py-1.5 text-sm border border-slate-300 rounded text-center tabular"
            />
            <span className="text-sm text-slate-600">days</span>
          </div>
        </div>

        <ToggleRow
          label="Customer can approve estimates"
          description="Allow customers to approve estimates from the customer portal."
          checked={settings.estimate_customer_can_approve !== false}
          onChange={(v) => patch({ estimate_customer_can_approve: v })}
          testid="est-customer-approve"
        />

        <ToggleRow
          label="Require e-signature for approval"
          description="Customers draw a signature on the portal to approve."
          checked={false}
          onChange={() => {}}
          disabled
          comingSoon
          testid="est-esig"
        />

        <ToggleRow
          label="Include images when printing or downloading estimates"
          description="Photos attached to line items are embedded in the estimate PDF."
          checked={!!settings.estimate_include_images}
          onChange={(v) => patch({ estimate_include_images: v })}
          testid="est-include-images"
        />

        <ToggleRow
          label="Automatically copy an approved estimate to a new job"
          description="When a customer approves an estimate, a job is created automatically."
          checked={!!settings.estimate_auto_copy_to_job}
          onChange={(v) => patch({ estimate_auto_copy_to_job: v })}
          testid="est-auto-copy"
        />
      </section>
    </div>
  );
}

function CustomerViewTab({ settings, patch }) {
  const cv = { ...DEFAULT_CV, ...(settings.estimate_customer_view || {}), fields: { ...DEFAULT_CV.fields, ...((settings.estimate_customer_view || {}).fields || {}) } };
  const setField = (k, v) => patch({ estimate_customer_view: { ...cv, fields: { ...cv.fields, [k]: v } } });
  const setFormat = (v) => patch({ estimate_customer_view: { ...cv, view_format: v } });

  return (
    <div className="grid lg:grid-cols-[1fr_400px] gap-6">
      {/* left = toggles */}
      <div className="space-y-4">
        <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
          These toggles control the on-screen preview on the right. Your existing multi-option estimate PDF continues to render as it does today.
        </div>

        <section data-testid="est-cv-job-invoice" className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Job and estimate</h3>
          <CheckboxRow label="Job number"        checked={cv.fields.job_number}        onChange={(v) => setField("job_number", v)}        testid="est-cv-job-number" />
          <CheckboxRow label="Estimate number"   checked={cv.fields.estimate_number}   onChange={(v) => setField("estimate_number", v)}   testid="est-cv-estimate-number" />
          <CheckboxRow label="Service date"      checked={cv.fields.service_date}      onChange={(v) => setField("service_date", v)}      testid="est-cv-service-date" />
          <CheckboxRow label="Estimate date"     checked={cv.fields.estimate_date}     onChange={(v) => setField("estimate_date", v)}     testid="est-cv-estimate-date" />
          <CheckboxRow label="Expiration date"   checked={cv.fields.expiration_date}   onChange={(v) => setField("expiration_date", v)}   testid="est-cv-expiration-date" />
          <CheckboxRow label="Summary of work"   checked={cv.fields.summary_of_work}   onChange={(v) => setField("summary_of_work", v)}   testid="est-cv-summary" />
        </section>

        <section data-testid="est-cv-format" className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="font-semibold text-slate-900 mb-3">View format</h3>
          <label className="flex items-center gap-2 py-1.5 cursor-pointer">
            <input type="radio" name="view_format" checked={cv.view_format === "email"} onChange={() => setFormat("email")} data-testid="est-cv-format-email" className="text-[#0F4C81] focus:ring-[#0F4C81]" />
            <span className="text-sm text-slate-900">Email optimized</span>
          </label>
          <label className="flex items-center gap-2 py-1.5 cursor-pointer">
            <input type="radio" name="view_format" checked={cv.view_format === "envelope"} onChange={() => setFormat("envelope")} data-testid="est-cv-format-envelope" className="text-[#0F4C81] focus:ring-[#0F4C81]" />
            <span className="text-sm text-slate-900">Envelope optimized</span>
          </label>
        </section>

        <section data-testid="est-cv-business" className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Business and customer</h3>
          <CheckboxRow label="Business name"          checked={cv.fields.business_name}          onChange={(v) => setField("business_name", v)}          testid="est-cv-business-name" />
          <CheckboxRow label="Technician name"        checked={cv.fields.technician_name}        onChange={(v) => setField("technician_name", v)}        testid="est-cv-technician-name" />
          <CheckboxRow label="Customer display name"  checked={cv.fields.customer_display_name}  onChange={(v) => setField("customer_display_name", v)}  testid="est-cv-customer-name" />
          <CheckboxRow label="Customer company name"  checked={cv.fields.customer_company_name}  onChange={(v) => setField("customer_company_name", v)}  testid="est-cv-customer-company" />
        </section>

        <section data-testid="est-cv-services" className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Services</h3>
          <CheckboxRow label="Line items"            checked={cv.fields.services_line_items} onChange={(v) => setField("services_line_items", v)} testid="est-cv-svc-line-items" />
          <CheckboxRow label="Services name"         checked={cv.fields.services_name}       onChange={(v) => setField("services_name", v)}       testid="est-cv-svc-name" />
          <CheckboxRow label="Description"           checked={cv.fields.services_description} onChange={(v) => setField("services_description", v)} testid="est-cv-svc-description" />
          <CheckboxRow label="Quantity"              checked={cv.fields.services_quantity}   onChange={(v) => setField("services_quantity", v)}   testid="est-cv-svc-quantity" />
          <CheckboxRow label="Unit price"            checked={cv.fields.services_unit_price} onChange={(v) => setField("services_unit_price", v)} testid="est-cv-svc-unit-price" />
          <CheckboxRow label="Line items amount"     checked={cv.fields.services_line_amount} onChange={(v) => setField("services_line_amount", v)} testid="est-cv-svc-line-amount" />
          <CheckboxRow label="Subtotal"              checked={cv.fields.services_subtotal}   onChange={(v) => setField("services_subtotal", v)}   testid="est-cv-svc-subtotal" />
        </section>

        <section data-testid="est-cv-materials" className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="font-semibold text-slate-900 mb-3">Materials</h3>
          <CheckboxRow label="Line items" checked={cv.fields.materials_line_items} onChange={(v) => setField("materials_line_items", v)} testid="est-cv-mat-line-items" />
          <CheckboxRow label="Subtotal"   checked={cv.fields.materials_subtotal}   onChange={(v) => setField("materials_subtotal", v)}   testid="est-cv-mat-subtotal" />
        </section>
      </div>

      {/* right = live preview */}
      <div className="lg:sticky lg:top-6 self-start">
        <div data-testid="est-cv-preview" className="bg-white border border-slate-200 rounded-lg p-5 text-xs">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-3">Estimate preview</div>
          <div className="border border-slate-200 rounded p-4 space-y-3">
            {cv.fields.business_name && <div className="font-display font-bold text-slate-900">{settings.business_name || "Your Business"}</div>}
            {cv.fields.estimate_number && <div className="text-slate-600">Estimate #12345</div>}
            {cv.fields.job_number && <div className="text-slate-600">Job #12345</div>}
            {cv.fields.customer_display_name && <div className="pt-2 text-slate-900">Estimate for Customer Name</div>}
            {cv.fields.customer_company_name && <div className="text-slate-500">Customer Company Ltd.</div>}
            <div className="text-slate-500">1234 Street Name, City</div>
            <div className="grid grid-cols-3 gap-2 pt-1 text-[11px]">
              {cv.fields.estimate_date && <div><div className="text-slate-400">Created</div><div className="text-slate-700">2026-02-13</div></div>}
              {cv.fields.expiration_date && <div><div className="text-slate-400">Expires</div><div className="text-slate-700">2026-03-15</div></div>}
              {cv.fields.service_date && <div><div className="text-slate-400">Serviced</div><div className="text-slate-700">2026-02-15</div></div>}
            </div>
            {cv.fields.technician_name && <div className="text-slate-500 pt-1">Technician: John Smith</div>}
            {cv.fields.summary_of_work && <div className="text-slate-500 italic pt-1">Summary of work…</div>}

            {cv.fields.services_line_items && (
              <div className="pt-2 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Services</div>
                <div className="flex justify-between text-[11px]">
                  <div>
                    {cv.fields.services_name && <div className="text-slate-900">Service name</div>}
                    {cv.fields.services_description && <div className="text-slate-500">Service description…</div>}
                  </div>
                  <div className="text-right">
                    {cv.fields.services_quantity && <div className="text-slate-700">Qty 1</div>}
                    {cv.fields.services_unit_price && <div className="text-slate-700 tabular">$0.00</div>}
                    {cv.fields.services_line_amount && <div className="text-slate-900 font-medium tabular">$0.00</div>}
                  </div>
                </div>
                {cv.fields.services_subtotal && <div className="flex justify-between pt-1 mt-1 border-t border-slate-100 text-slate-900 font-medium"><span>Services subtotal</span><span className="tabular">$0.00</span></div>}
              </div>
            )}

            {cv.fields.materials_line_items && (
              <div className="pt-2 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Materials</div>
                <div className="text-[11px] text-slate-500">Line items…</div>
                {cv.fields.materials_subtotal && <div className="flex justify-between pt-1 mt-1 border-t border-slate-100 text-slate-900 font-medium"><span>Materials subtotal</span><span className="tabular">$0.00</span></div>}
              </div>
            )}

            <div className="pt-2 border-t border-slate-100 flex justify-between text-slate-900 font-bold">
              <span>Total</span>
              <span className="tabular">$0.00</span>
            </div>
          </div>
          <div className="text-[10px] text-slate-400 mt-2">Format: {cv.view_format === "email" ? "Email optimized" : "Envelope optimized"}</div>
        </div>
      </div>
    </div>
  );
}
